import React from "react";
import { computed, observable, makeObservable, action, runInAction } from "mobx";
import { MrbBaseViewStore } from "mrb/core";
import { nakiRadioSystemTypes } from "common/constants";
import { find, isNil } from "lodash";

class HomeSubPodcastViewStore extends MrbBaseViewStore {
    @observable isCollapsed = false;
    @observable isUpdating = false;

    panelRef = React.createRef();
    subPodcastId = null;

    @computed get coverImageUrl() {
        if (this.subPodcast && this.subPodcast.coverImageId) {
            return this.homePodcastViewStore.routeStore.createPodcastCoverImageUrl(
                this.subPodcast.coverImageId,
                50,
                50
            );
        }

        return null;
    }

    @computed get selectedDevice() {
        return this.rootStore.userDeviceStore?.selectedDevice?.device;
    }

    @computed get isDisabled() {
        return this.homePodcastViewStore.updatingStatus || this.isUpdating;
    }
    
    @computed get subPodcast() {
        if (this.homePodcastViewStore.item && this.homePodcastViewStore.item.subPodcasts) {
            return find(
                this.homePodcastViewStore.item.subPodcasts,
                (subPodcast) => subPodcast.id === this.subPodcastId
            );
        }
        return null;
    }

    @computed get contentAccessResult() {
        return this.evaluateContentAccess(this.subPodcast);
    }

    @computed get subPodcastStatusItem() {
        if (this.subPodcast) {
            return this.subPodcast.userDevicePodcast;
        }
        return null;
    }

    @computed get isTurnedOn() {
        if (!isNil(this.subPodcastStatusItem)) {
            return this.subPodcastStatusItem.isTurnedOn;
        } else if (this.subPodcast) {
            const selectedDevice = this.rootStore.userDeviceStore.selectedDevice;
            if (selectedDevice && selectedDevice.device && selectedDevice.device.systemType) {
                switch (selectedDevice.device.systemType.abrv) {
                    case nakiRadioSystemTypes.venice:
                        return this.subPodcast.isTurnedOnByDefault;
                    case nakiRadioSystemTypes.mobile:
                    case nakiRadioSystemTypes.mobile_restricted:
                    case nakiRadioSystemTypes.mobile_handheld:
                        return this.subPodcast.isTurnedOnByDefaultInApp;
                    case nakiRadioSystemTypes.mobile_embedded:
                        return this.subPodcast.isTurnedOnByDefaultInEmbeddedApp;
                    default:
                        break;
                }
            }
        }
        return false;
    }

    constructor(rootStore, { subPodcastId, homePodcastViewStore }) {
        super(rootStore);
        makeObservable(this);
        this.subPodcastId = subPodcastId;
        this.homePodcastViewStore = homePodcastViewStore;

        this.reaction(
            () => this.isCollapsed,
            (isCollapsed) => {
                if (isCollapsed && this.panelRef) {
                    // We need to execute scroll function asynchronously in order to take effect.
                    setTimeout(() => {
                        this.panelRef.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    });
                }
            }
        );
    }

    initialize() {
        const subPodcastId = this.rootStore.routerStore.routerState.queryParams.subPodcastId;
        if (subPodcastId && this.subPodcast.id === subPodcastId) {
            runInAction(() => {
                this.isCollapsed = true;
            });
        }
        return Promise.resolve();
    }

    @action.bound
    async toggleSubPodcastStatus(isTurnedOn) {
        this.isCollapsed = !this.isCollapsed;

        this.setIsUpdating(true);
        await this.homePodcastViewStore.updateSubPodcastTurnedOnStatus(this.subPodcast, isTurnedOn);
        this.setIsUpdating(false);
    }

    @action.bound
    setIsUpdating(isUpdating) {
        this.isUpdating = isUpdating;
    }

    @action.bound
    toggleCollapse() {
        this.isCollapsed = !this.isCollapsed;
    }

    setPanelRef = (ref) => {
        this.panelRef = ref;
    };

    evaluateContentAccess(item) {
        if (isNil(item)) {
            return false;
        }
        return this.rootStore.contentAccessValidator.canAccessPodcast({
            isPremium: item.isPremium,
            legacySubscriptionExpirationDate: item.legacySubscriptionExpirationDate,
            unavailableInMobileApp: item.isVisibleInApp === false,
            unavailableInNakiTouch: item.isVisibleInEmbeddedApp === false,
        });
    }
}

export default HomeSubPodcastViewStore;
